/* filepath: /Users/george/MatrizInc/MarketBrewer/Clients/street-lawyer-services/website/src/core/Pages/HomePage/AboutSection/index.tsx */
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  animateVideoContainer,
  animateContent,
  cleanupScrollTriggers,
} from "./gsap";

const AboutSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // YouTube video ID for the link
  const youtubeVideoId = "Y-QjLUhdGdA";
  const youtubeUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;

  useEffect(() => {
    // Apply animations with a small delay to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      // Animate the video container from left
      animateVideoContainer(
        { current: videoContainerRef.current },
        { current: sectionRef.current }
      );

      // Animate the content from right with a slight delay
      animateContent(
        { current: contentRef.current },
        { current: sectionRef.current }
      );
    }, 100);

    // Cleanup animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  // Image sources with fallbacks
  const imgSrc = "/images/lonny-the-street-lawyer-cannabis-talk-poster.jpg";
  const webpSrc = "/images/lonny-the-street-lawyer-cannabis-talk-poster.webp";

  const handleImageLoad = () => {
    console.log("✅ Image loaded successfully");
    setImageLoaded(true);
  };

  const handleImageError = () => {
    console.error("❌ Image failed to load");
    setImageError(true);
  };

  return (
    <section ref={sectionRef} className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div ref={videoContainerRef} className="md:w-1/2 w-full">
            {/* Video thumbnail with play button overlay instead of iframe */}
            <div className="relative w-full rounded-lg shadow-lg overflow-hidden group">
              <a
                href={youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch interview with Lonny Bramzon, founder of Street Lawyer Services"
                className="block relative aspect-video"
              >
                {/* Loading indicator */}
                {!imageLoaded && !imageError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {/* Fallback for image error */}
                {imageError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-200 p-4 text-center">
                    <svg
                      className="w-12 h-12 text-cannabis-green-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      ></path>
                    </svg>
                    <p className="font-medium text-gray-900">
                      Image could not be loaded
                    </p>
                    <p className="text-sm text-gray-600">
                      Click to watch the interview with Lonny Bramzon
                    </p>
                  </div>
                )}

                {/* First try with direct img for better reliability */}
                <img
                  src={imgSrc}
                  alt="Lonny Bramzon interview - Street Lawyer Services Cannabis Dispensary"
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                    imageLoaded ? "block" : "hidden"
                  }`}
                  width="640"
                  height="360"
                  loading="eager"
                  fetchpriority="high"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />

                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-all duration-300">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-cannabis-green-500/90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="white"
                      className="w-8 h-8 md:w-10 md:h-10 ml-1"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </a>
            </div>

            {/* SEO-optimized caption for the video */}
            <p className="text-sm text-gray-500 mt-2 italic text-center">
              Interview with Lonny Bramzon (The Street Lawyer) - Founder of DC's
              Premier Cannabis Dispensary
            </p>
          </div>

          <div ref={contentRef} className="md:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              About Street Lawyer Services
            </h2>
            <p className="text-gray-700 mb-4">
              Street Lawyer Services is DC's premier cannabis dispensary,
              committed to providing high-quality products and exceptional
              customer service to adult consumers.
            </p>
            <p className="text-gray-700 mb-6">
              Our knowledgeable staff is dedicated to helping you find the right
              products for your needs while creating a welcoming environment for
              our community.
            </p>
            <p className="text-gray-700 mb-6">
              Watch our founder Lonny Bramzon discuss his journey from public
              defender to cannabis advocate and how Street Lawyer Services
              creates a welcoming community experience in Washington DC.
            </p>
            <Link to="/about" className="btn-primary">
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Debug/test image - delete after confirming it loads */}
      <div style={{ display: "none" }}>
        <img
          src={imgSrc}
          alt="Test image load"
          onLoad={() => console.log("Test image loaded successfully")}
          onError={() => console.error("Test image failed to load")}
        />
      </div>
    </section>
  );
};

export default AboutSection;
